import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 cursor-pointer" onClick={closeMobile}>
            <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 tracking-tight">
              Kakobuy<span className="text-primary-500">Polska</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            <Link to="/" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Strona główna
            </Link>
            <a href="#categories" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Kategorie
            </a>
            <Link to="/poradnik" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Poradnik zakupowy
            </Link>
            <Link to="/bezpieczenstwo" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Bezpieczeństwo
            </Link>
            <Link to="/porownanie" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Porównanie
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Kakobuy Spreadsheet
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden py-4 border-t border-background-200/70 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={closeMobile}
                className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-home-line text-base w-5 h-5 flex items-center justify-center"></i>
                <span>Strona główna</span>
              </Link>

              <div className="mt-2 mb-1 px-3">
                <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                  Kategorie produktów
                </span>
              </div>

              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  <span>{cat.name}</span>
                  <span className="text-xs text-foreground-400 ml-auto">{cat.namePl}</span>
                  <i className="ri-external-link-line text-xs text-foreground-400"></i>
                </a>
              ))}

              <div className="mt-3 mb-1 px-3">
                <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                  Strony
                </span>
              </div>

              <Link
                to="/poradnik"
                onClick={closeMobile}
                className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line text-base w-5 h-5 flex items-center justify-center"></i>
                <span>Poradnik zakupowy</span>
              </Link>

              <Link
                to="/bezpieczenstwo"
                onClick={closeMobile}
                className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-shield-check-line text-base w-5 h-5 flex items-center justify-center"></i>
                <span>Bezpieczeństwo</span>
              </Link>

              <Link
                to="/porownanie"
                onClick={closeMobile}
                className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-scales-line text-base w-5 h-5 flex items-center justify-center"></i>
                <span>Porównanie</span>
              </Link>

              <div className="mt-3 pt-3 border-t border-background-200/70 px-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  Kakobuy Spreadsheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>

                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 mt-2 bg-accent-500 text-background-50 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer"
                >
                  Kup teraz
                  <i className="ri-shopping-cart-line"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}